/**
 * CreateTaskModal — shared "Create Task" lightbox (Phase 5 / 2B).
 *
 * Single source of truth used by both:
 *   - Communications → Tasks tab (`Communications.jsx`)
 *   - Lead Focus → Tasks tab (`LeadFocus.jsx` via `LeadFocusTabs::TasksTab`)
 *
 * Persists to POST /api/tasks. Caller passes optional `defaultLead` to
 * pre-bind the new task to a lead.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Plus } from "lucide-react";
import { API, useAuth } from "@/App";
import { LeadLookupField } from "@/components/LeadLookupField";
import { TaskTimeInput } from "@/components/TaskDueTime";
import { buildDueTimeFields } from "@/lib/taskDue";

export const TASK_PRIORITIES = [
  { id: "low", label: "Low" },
  { id: "medium", label: "Medium" },
  { id: "high", label: "High" },
  { id: "urgent", label: "Urgent" },
];

export const TASK_CATEGORIES = ["follow_up", "call", "demo", "research", "outreach", "admin", "other"];
export const TASK_CAT_LABELS = {
  follow_up: "Follow-up",
  call: "Call",
  demo: "Demo",
  research: "Research",
  outreach: "Outreach",
  admin: "Admin",
  other: "Other",
};

// Display name for a pre-bound lead across the CRM's lead shapes
// (Lead Focus uses `clinic`; its loading placeholder is never a name).
const leadDisplayName = (l) => {
  if (!l?.id) return "";
  const person = l.first_name ? `${l.first_name} ${l.last_name || ""}`.trim() : "";
  const n = [l.clinic, l.company, l.title, person, l.contact_name].find((v) => v && v !== "Loading…");
  return n || "";
};

export default function CreateTaskModal({ open, onClose, onCreated, defaultLead = null, defaultTaskPrefill = null }) {
  const { token, user } = useAuth();
  const [form, setForm] = useState({
    title: defaultTaskPrefill?.title || "",
    description: defaultTaskPrefill?.description || "",
    priority: defaultTaskPrefill?.priority || "medium",
    category: defaultTaskPrefill?.category || "follow_up",
    due_date: defaultTaskPrefill?.due_date || "",
    due_time: "",
    // Iter 260 (Jul 2026, Fix K) — track BOTH the user id (for the
    // backend `assigned_to` field) and the display name. Default to
    // the current user assigning to themselves.
    assigned_to: user?.id || "",
    assigned_to_name: user?.full_name || user?.name || user?.email || "",
    lead_id: leadDisplayName(defaultLead) ? defaultLead.id : "",
    lead_name: leadDisplayName(defaultLead),
  });
  const [saving, setSaving] = useState(false);
  const [leadText, setLeadText] = useState("");
  // Iter 260 (Jul 2026, Fix K) — real user picker sourced from
  // /api/admin/users. Previously the modal used a free-text `<input>`
  // for the assignee, which never resolved to a UUID → task landed in
  // the DB with `assigned_to: null`, invisible to the Tasks page's
  // "assigned to me" filter. Now the assignee dropdown is populated
  // from the actual users list and always sends a real UUID.
  const [teammates, setTeammates] = useState([]);
  const [teammatesErr, setTeammatesErr] = useState(false);

  // Reset when re-opened with a different defaultLead OR a new
  // defaultTaskPrefill (Iter 210.15 — dialer opens the modal with a
  // pre-filled proposal from the AI auto-map strip).
  useEffect(() => {
    if (open) {
      setForm({
        title: defaultTaskPrefill?.title || "",
        description: defaultTaskPrefill?.description || "",
        priority: defaultTaskPrefill?.priority || "medium",
        category: defaultTaskPrefill?.category || "follow_up",
        due_date: defaultTaskPrefill?.due_date || "",
        due_time: "",
        assigned_to: user?.id || "",
        assigned_to_name: user?.full_name || user?.name || user?.email || "",
        lead_id: leadDisplayName(defaultLead) ? defaultLead.id : "",
        lead_name: leadDisplayName(defaultLead),
      });
      setLeadText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultLead?.id, defaultTaskPrefill?.title, defaultTaskPrefill?.description, defaultTaskPrefill?.due_date]);

  // Fetch teammates list once the modal opens. Falls back gracefully
  // if the current user lacks admin access — non-admins get a free
  // input so nothing that used to work stops working.
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
        });
        if (cancelled) return;
        const rows = (Array.isArray(r.data) ? r.data : (r.data?.items || []))
          .filter(u => u.status !== "disabled")
          .sort((a, b) => (a.full_name || a.email || "").localeCompare(b.full_name || b.email || ""));
        setTeammates(rows);
      } catch (_) {
        if (!cancelled) setTeammatesErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/tasks`, {
        title: form.title.trim(),
        description: form.description,
        priority: form.priority,
        category: form.category,
        due_date: form.due_date || null,
        ...buildDueTimeFields(form.due_date, form.due_time),
        // Iter 260 (Jul 2026, Fix K) — send the resolved UUID plus
        // the display name. Backend Fix J also resolves name→UUID as a
        // safety net for API callers that only send the name.
        assigned_to: form.assigned_to || null,
        assigned_to_name: form.assigned_to_name,
        lead_id: form.lead_id || null,
        lead_name: form.lead_id ? form.lead_name : "",
        status: "open",
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Task created");
      if (!form.lead_id && leadText.trim()) toast.info("Saved without a lead — pick a lead from the list to link one.");
      // Iter 228 (spec §2C, §2G) — Lock-warning toast. When the
      // backend detects the task points at a lead currently owned
      // by a different rep, surface an amber notice explaining why
      // the task might not appear in the assignee's Personal
      // Campaign until ownership transfers.
      if (r?.data?.warning?.code === "lead_locked_to_other") {
        toast.warning(r.data.warning.message, { duration: 12000 });
      }
      onCreated?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create task");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}
      data-testid="create-task-modal"
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface, #fff)", borderRadius: 14, width: 480, maxWidth: "92vw", border: "0.5px solid var(--border, #eee)", boxShadow: "0 20px 60px rgba(0,0,0,0.20)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "0.5px solid var(--border, #eee)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Plus style={{ width: 14, height: 14, color: "var(--blue, #2563eb)" }} />
            <span style={{ fontSize: 14, fontWeight: 650 }}>Create task</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }} data-testid="create-task-close">
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div style={{ padding: 18 }}>
          <Field label="Title *">
            <input
              data-testid="task-title-input"
              className="input"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Task title…"
              autoFocus
            />
          </Field>
          <Field label="Description">
            <textarea
              className="input"
              rows={2}
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Details…"
              style={{ resize: "vertical" }}
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Field label="Priority">
              <select className="input" value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))} data-testid="task-priority">
                {TASK_PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select className="input" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} data-testid="task-category">
                {TASK_CATEGORIES.map(c => <option key={c} value={c}>{TASK_CAT_LABELS[c]}</option>)}
              </select>
            </Field>
            <Field label="Due date">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start" }}>
                <input type="date" className="input" value={form.due_date} onChange={(e) => { const v = e.target.value; setForm(f => ({ ...f, due_date: v, due_time: v ? f.due_time : "" })); }} data-testid="task-due" style={{ flex: "1 1 130px", minWidth: 0 }} />
                <div style={{ flex: "1 1 110px", minWidth: 0 }}>
                  <TaskTimeInput date={form.due_date} value={form.due_time} onChange={(v) => setForm(f => ({ ...f, due_time: v }))} />
                </div>
              </div>
            </Field>
            <Field label="Assign to">
              {/* Iter 260 (Jul 2026, Fix K) — user picker dropdown.
                  Falls back to a free-text input if the users list
                  fails to load (e.g. non-admin caller) so nothing
                  that used to work stops working. */}
              {teammates.length > 0 ? (
                <select
                  className="input"
                  data-testid="task-assignee"
                  value={form.assigned_to || ""}
                  onChange={(e) => {
                    const uid = e.target.value;
                    const picked = teammates.find(u => u.id === uid);
                    setForm(f => ({
                      ...f,
                      assigned_to: uid,
                      assigned_to_name: picked ? (picked.full_name || picked.email || "") : "",
                    }));
                  }}
                >
                  <option value="">— Select assignee —</option>
                  {teammates.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.full_name && u.full_name !== u.email
                        ? `${u.full_name} · ${u.email}`
                        : u.email}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  value={form.assigned_to_name}
                  onChange={(e) => setForm(f => ({ ...f, assigned_to_name: e.target.value }))}
                  placeholder={teammatesErr ? "Rep name or email…" : "Loading teammates…"}
                  data-testid="task-assignee"
                />
              )}
            </Field>
          </div>
          <Field label="Related lead">
            <LeadLookupField
              value={form.lead_id ? { id: form.lead_id, name: form.lead_name } : null}
              onChange={(l) => setForm(f => ({ ...f, lead_id: l?.id || "", lead_name: l?.name || "" }))}
              onTextChange={setLeadText}
              testid="task-related-lead"
            />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button onClick={onClose} className="btn" style={{ fontSize: 12 }}>Cancel</button>
            <button onClick={save} className="btn btn-primary" style={{ fontSize: 12 }} disabled={saving} data-testid="save-task-btn">
              {saving ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : null}
              {saving ? "Creating…" : "Create Task"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
